<template>
    <div class="c-button-group-wrapper">
        <div class="btn-group btn-group-toggle" :class="{ wide }" data-toggle="buttons">
            <label
                v-for="(item, idx) of items"
                :key="`c-group-item-${item.label}-${idx}`"
                class="btn btn-item"
                :class="{
                    'no-space mx-0': !separate,
                    'is-invalid': error,
                    [`btn-${size}`]: true,
                    pill: pill,
                    active: isItemActive(item),
                    'font-weight-bold': !noIcon,
                    disabled: item.disabled || disabled
                }"
            >
                <font-awesome-icon v-if="!noIcon" :icon="itemIcon(item)" class="mr-2" />
                <input
                    v-model="selectedValues"
                    :type="type"
                    :value="item.value"
                    :disabled="item.disabled || disabled"
                />
                {{ item.label }}
            </label>
        </div>
        <c-error v-if="error" :message="errorMsg" />
    </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { CButtonGroupItem } from '.';
import CError from '@/components/shared/CError.vue';

const props = withDefaults(
    defineProps<{
        items: CButtonGroupItem[];
        value?: unknown;
        type?: 'radio' | 'checkbox';
        size?: 'sm' | 'md' | 'lg';
        disabled?: boolean;
        separate?: boolean;
        selectedIcon?: string;
        initialIcon?: string;
        pill?: boolean;
        noIcon?: boolean;
        error?: boolean;
        errorMsg?: string;
        wide?: boolean;
    }>(),
    {
        type: 'checkbox',
        size: 'md',
        initialIcon: 'fa-solid fa-plus',
        selectedIcon: 'fa-solid fa-check'
    }
);

const emit = defineEmits(['input']);

const selectedValues = computed({
    get() {
        return props.value;
    },
    set(value: unknown) {
        emit('input', value);
    }
});

const isItemActive = (item: CButtonGroupItem) => {
    return Array.isArray(selectedValues.value)
        ? selectedValues.value.includes(item.value)
        : selectedValues.value === item.value;
};

const itemIcon = (item: CButtonGroupItem) => {
    if (
        selectedValues.value === item.value ||
        (Array.isArray(selectedValues.value) && selectedValues.value.includes(item.value))
    ) {
        return item.selectedIcon || props.selectedIcon;
    }

    return item.initialIcon || props.initialIcon;
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-button-group-wrapper {
    .btn-group {
        &.wide {
            display: flex !important;
            .btn-item {
                flex: 1;
                justify-content: center;
            }
        }
        .btn-item {
            display: flex;
            align-items: center;
            margin: 0 0.25rem;
            border: 1px solid get-color('gray');
            box-shadow: none !important;

            &:first-child {
                margin-left: 0;
            }
            &:last-child {
                margin-right: 0;
            }
            &:hover {
                background-color: get-color('gray-100');
            }
            &.pill {
                border-radius: 50rem !important;
            }
            &.no-space {
                &:not(:first-child) {
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-left-width: 0.5px;
                }
                &:not(:last-child) {
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    border-right-width: 0.5px;
                }
            }

            &.active {
                color: get-color('purple');
                border-color: get-color('purple');
                border-width: 1px;
                font-weight: bold;
            }

            &.is-invalid {
                border-color: get-color('red');
            }

            &.disabled {
                background-color: get-color('gray-100');
            }
        }
    }
}
</style>
